// import React, { useState, useEffect } from 'react';
import { useState, useEffect } from 'react';

// Define type for feedback entries (could also go in types.ts)
interface FeedbackEntry {
  id: string;
  user_id: string | null;
  submitter_username: string | null; // Joined from users table
  feedback_type: 'bug' | 'suggestion' | 'general';
  message: string;
  submitted_at: string;
}

export default function AdminFeedback() {
  const [feedback, setFeedback] = useState<FeedbackEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchFeedback();
  }, []);

  const fetchFeedback = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch('/api/admin/feedback', { credentials: 'include' });
      const data = await response.json();
      if (!response.ok) {
        throw new Error((data as {error: string})?.error || 'Failed to fetch feedback');
      }
      setFeedback(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load feedback');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white">User Feedback</h1>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="shadow-md dark:shadow-none rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
         <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Submitted</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Message</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <tr><td colSpan={4} className="text-center p-4 dark:text-gray-400">Loading...</td></tr>
              ) : feedback.length === 0 ? (
                <tr><td colSpan={4} className="text-center p-4 dark:text-gray-400">No feedback submitted yet.</td></tr>
              ) : (
                feedback.map(entry => (
                  <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{new Date(entry.submitted_at).toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400"><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${entry.feedback_type === 'bug' ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' : entry.feedback_type === 'suggestion' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}`}>{entry.feedback_type}</span></td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{entry.submitter_username || 'Anonymous/Deleted'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{entry.message}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
} 